import useProcessInvoiceTransactionMutation from '@/api/mutations/useProcessInvoiceTransactionMutation';
import useFindInvoiceTransactionsQuery from '@/api/queries/useFindInvoiceTransactionsQuery';
import Button from '@/components/Button';
import CustomToast from '@/components/CustomToast';
import PageHeader from '@/components/PageHeader';
import { InvoiceTransaction } from '@/models/Invoice';
import Breadcrumbs from '@/modules/admin/components/Breadcrumbs';
import InvoiceTransactionTable from '@/modules/admin/components/InvoiceTransactionTable';
import { AdminBillingHomePath } from '@/modules/admin/paths';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';

const BillingFailedTransactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const processInvoiceTransaction = useProcessInvoiceTransactionMutation();
  const page = Math.max(Number(searchParams.get('page')) || 1, 1);
  const { invoiceTransactions, isLoading, isFetching, error, itemsPerPage } =
    useFindInvoiceTransactionsQuery(page, {
      status: 'failed',
    });

  const updateSearchParams = (nextPage: number) => {
    const params = new URLSearchParams();

    if (nextPage > 1) {
      params.set('page', String(nextPage));
    }

    setSearchParams(params);
  };

  const handleRetryFailedTransaction = async (invoiceTransaction: InvoiceTransaction) => {
    try {
      await processInvoiceTransaction.mutateAsync(invoiceTransaction.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Retried transaction ${invoiceTransaction.id}.`}
          visible={t.visible}
        />
      ));
    } catch (retryError) {
      const message =
        retryError instanceof Error && retryError.message
          ? retryError.message
          : 'Failed to retry transaction.';

      toast.custom((t) => (
        <CustomToast id={t.id} message={message} variant="error" visible={t.visible} />
      ));

      console.error('Failed to retry invoice transaction:', retryError);
    }
  };

  return (
    <>
      <Breadcrumbs />

      <PageHeader title="Failed Transactions">
        <Button to={AdminBillingHomePath} variant="secondary">
          Billing Home
        </Button>
      </PageHeader>

      {error ? (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          Failed to load failed transactions.
        </div>
      ) : (
        <InvoiceTransactionTable
          invoiceTransactions={invoiceTransactions.items}
          page={page}
          itemsPerPage={itemsPerPage}
          totalCount={invoiceTransactions.totalCount}
          estimatedTotalCount={invoiceTransactions.estimatedTotalCount}
          isLoading={isLoading}
          isFetching={isFetching}
          retryingTransactionId={
            processInvoiceTransaction.isPending ? processInvoiceTransaction.variables : undefined
          }
          onChangePage={updateSearchParams}
          onRetry={handleRetryFailedTransaction}
        />
      )}
    </>
  );
};

export default BillingFailedTransactions;
